import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../assets/LoadingSpinner';
import { AuthContext } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { useDocumentTypesContext } from '../context/DocumentTypesContext';
import { modulesForDocumentType } from '../constants/documentModule';
import { getModuleForPath, isPlanningRoute, isAdminOnlyModule, PLANNING_MODULES } from '../constants/modules';


interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { user, isLoading } = useContext(AuthContext);
    const { hasAccess, hasDocumentAccess, isAdmin } = usePermissions();
    const location = useLocation();
    const { allDocumentTypes, allDocumentTypesLoaded } = useDocumentTypesContext();

    // A cached user is already available synchronously while AuthProvider
    // revalidates the session. Let the screen own the visible loading state so
    // refresh does not show an auth spinner followed by a second data spinner.
    if (isLoading && !user) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingSpinner />
            </div>
        );
    }

    // Not logged in - redirect to login
    if (!user) {
        return <Navigate to="/" replace />;
    }

    // Check if user must change password
    if (user.mustChangePassword && location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    // Planning routes: allow if user has access to ANY planning sub-module
    if (isPlanningRoute(location.pathname)) {
        const hasAnyPlanning = PLANNING_MODULES.some(m => hasAccess(m));
        if (!hasAnyPlanning) {
            return <Navigate to="/dashboard" replace />;
        }
        return <>{children}</>;
    }

    // Devices: single merged entry; allow if user has DEVICES or legacy PRINTERS/PHONES/COMPUTERS
    if (location.pathname === '/computer-system/devices') {
        const hasDevicesAccess = hasAccess('DEVICES') || hasAccess('PRINTERS') || hasAccess('PHONES') || hasAccess('COMPUTERS');
        if (!hasDevicesAccess) {
            return <Navigate to="/dashboard" replace />;
        }
        return <>{children}</>;
    }

    // Per-document access: /work-order/:code requires read access to that document type
    const workOrderMatch = location.pathname.match(/^\/work-order\/([^/]+)$/);
    if (workOrderMatch) {
        const docCode = workOrderMatch[1];
        if (!hasDocumentAccess('WORK_ORDERS', docCode, 'read')) {
            return <Navigate to="/dashboard" replace />;
        }
        return <>{children}</>;
    }

    // Document screens: the module follows the document type (server rule), not the URL prefix.
    const docPathMatch = location.pathname.match(/^\/(?:material|non-material|invoice|packing-list|procurement)\/.+\/([^/]+)$/)
        || location.pathname.match(/^\/(?:invoice|packing-list|procurement)\/([^/]+)$/);
    if (docPathMatch) {
        if (!allDocumentTypesLoaded) {
            return (
                <div className="flex items-center justify-center h-screen">
                    <LoadingSpinner />
                </div>
            );
        }
        const docType = allDocumentTypes.find((d) => d.code === docPathMatch[1]);
        if (docType) {
            if (!modulesForDocumentType(docType).some((m) => hasDocumentAccess(m, docType.code, 'read'))) {
                return <Navigate to="/dashboard" replace />;
            }
            return <>{children}</>;
        }
    }

    // Check module access
    const requiredModule = getModuleForPath(location.pathname);
    if (requiredModule) {
        // Admin-only modules (USERS, SECURITY, COMPANY_SETTINGS, DATABASE, SECTORS)
        // require the admin role — a module grant alone is not enough.
        if (isAdminOnlyModule(requiredModule) && !isAdmin) {
            return <Navigate to="/dashboard" replace />;
        }
        if (!hasAccess(requiredModule)) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return <>{children}</>;
};

export default ProtectedRoute;
